import { createTheme as createMuiTheme, Theme, ThemeOptions } from '@mui/material';
import merge from 'deepmerge';
import _ from 'lodash';
import { DASHBOARD_BACKGROUND_COLORS, RESQ_BRAND_COLORS } from 'src/constants/palette';

export type ThemeMode = 'light' | 'dark';

type ColorRole = 'primary' | 'secondary' | 'critical' | 'success';

/**
 * Menus, select lists, popovers and search dropdowns float above the page, so unlike
 * resting surfaces they keep a border and shadow to show depth.
 */
export const getFloatingSurfaceStyles = (theme: Theme) => ({
    backgroundColor: theme.resqPalette.surface.default,
    color: theme.resqPalette.text.default,
    border: `1px solid ${theme.resqPalette.border.subdued}`,
    // A string, not a number: `sx` multiplies a bare number by theme.shape.borderRadius (8 -> 32px).
    borderRadius: '8px',
    boxShadow:
        theme.palette.mode === 'dark'
            ? '0px 8px 24px rgba(0, 0, 0, 0.56), 0px 2px 6px rgba(0, 0, 0, 0.4)'
            : '0px 8px 24px rgba(16, 24, 40, 0.12), 0px 2px 6px rgba(16, 24, 40, 0.08)',
});

/** Paper nested inside a floating surface (e.g. an inline Autocomplete list); the parent draws the edge. */
export const embeddedSurfaceStyles = {
    margin: 0,
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
} as const;

const DEFAULT_TEXT_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'body1', 'body2'] as const;
const SUBDUED_TEXT_VARIANTS = ['subtitle1', 'subtitle2', 'caption', 'body3'] as const;

/**
 * Base typography variants bake in the light navy and slate text colors. MUI parts that spread a
 * variant (such as the pagination labels) use that color directly, so each theme recolors them.
 */
export const withTypographyTextColors = (
    typography: Theme['typography'],
    colors: { default: string; subdued: string },
) => {
    const recolor = (variants: readonly (keyof Theme['typography'])[], color: string) =>
        Object.fromEntries(
            variants.map((variant) => [variant, { ...(typography[variant] as object), color }]),
        );

    return {
        ...typography,
        ...recolor(DEFAULT_TEXT_VARIANTS, colors.default),
        ...recolor(SUBDUED_TEXT_VARIANTS, colors.subdued),
    };
};

type ThemeComponents = NonNullable<Theme['components']>;
type ComponentOverride = {
    defaultProps?: object;
    styleOverrides?: Record<string, unknown>;
};

const resolveStyleOverride = (style: unknown, props: object) =>
    (typeof style === 'function' ? style(props) : style) as object;

/**
 * Layers a dashboard's component overrides on top of the base theme's instead of replacing them,
 * so base defaultProps and style slots stay in place. When both set the same slot, the two are
 * resolved against the rendering theme and deep merged, and the dashboard's values win.
 */
export const mergeComponentOverrides = (
    base: ThemeComponents | undefined,
    overrides: ThemeComponents,
): ThemeComponents => {
    const merged = { ...base } as Record<string, ComponentOverride>;

    for (const [name, override] of Object.entries(overrides) as [string, ComponentOverride][]) {
        const baseOverride = merged[name] ?? {};
        const styleOverrides = { ...baseOverride.styleOverrides };

        for (const [slot, style] of Object.entries(override.styleOverrides ?? {})) {
            const baseStyle = styleOverrides[slot];
            if (baseStyle === undefined) {
                styleOverrides[slot] = style;
            } else if (typeof baseStyle !== 'function' && typeof style !== 'function') {
                styleOverrides[slot] = merge(baseStyle as object, style as object);
            } else {
                styleOverrides[slot] = (props: object) =>
                    merge(
                        resolveStyleOverride(baseStyle, props),
                        resolveStyleOverride(style, props),
                    );
            }
        }

        merged[name] = {
            ...baseOverride,
            ...override,
            defaultProps: { ...baseOverride.defaultProps, ...override.defaultProps },
            styleOverrides,
        };
    }

    return merged as ThemeComponents;
};

export const getActionPalette = (mode: ThemeMode = 'light') => {
    const isDark = mode === 'dark';
    const primaryText = isDark ? '#8AB4F8' : '#0058CA';
    const primaryTextPressed = isDark ? '#AECBFA' : '#004299';
    const primaryTextDisabled = isDark ? '#5F7FAE' : '#81AEE8';
    const primaryBorder = isDark ? '#8AB4F8' : '#2A76D9';
    const primaryBorderPressed = isDark ? '#AECBFA' : '#004299';
    const primaryBorderDisabled = isDark ? '#3F5F8F' : '#81AEE8';
    // A light disabled fill glares on dark canvases and leaves white labels at ~2.3:1.
    const primaryDisabled = isDark ? '#3F5F8F' : '#81AEE8';
    const secondarySurface = isDark ? '#3D3E42' : '#D4D4D7';
    const secondaryHovered = isDark ? '#55565A' : '#AAABB0';
    const secondaryPressed = isDark ? '#6D6E72' : '#909097';
    const secondaryDisabled = isDark ? '#242A33' : '#F0F1F1';
    const secondaryBorder = isDark ? '#5F6368' : '#D4D4D7';
    const secondaryBorderPressed = isDark ? '#80868B' : '#909097';
    const secondaryBorderDisabled = isDark ? '#3C4043' : '#F0F1F1';
    const secondaryOn = isDark ? RESQ_BRAND_COLORS.cream : RESQ_BRAND_COLORS.navy;

    return {
        primary: {
            mui: {
                // Darkened slightly from #2A76D9 so white labels reach 4.5:1.
                main: '#2673D7',
                dark: '#004299',
                contrastText: '#FFFFFF',
            },
            surface: '#2673D7',
            hovered: '#0058CA',
            pressed: '#004299',
            disabled: primaryDisabled,
            subduedHovered: '#2A76D91A',
            subduedPressed: '#2A76D94D',
            text: primaryText,
            textPressed: primaryTextPressed,
            textDisabled: primaryTextDisabled,
            onText: '#FFFFFF',
            icon: primaryText,
            iconDisabled: primaryTextDisabled,
            border: primaryBorder,
            borderPressed: primaryBorderPressed,
            borderDisabled: primaryBorderDisabled,
        },
        secondary: {
            mui: {
                main: secondarySurface,
                dark: secondaryPressed,
                contrastText: secondaryOn,
            },
            surface: secondarySurface,
            hovered: secondaryHovered,
            pressed: secondaryPressed,
            disabled: secondaryDisabled,
            text: secondaryOn,
            onText: secondaryOn,
            border: secondaryBorder,
            borderPressed: secondaryBorderPressed,
            borderDisabled: secondaryBorderDisabled,
        },
    };
};

export interface ThemePalette {
    brand: {
        orange: string;
        navy: string;
        cream: string;
        rust: string;
        slate: string;
    };
    background: {
        default: string;
    };
    surface: {
        getBase(colorRole: ColorRole): string;
        getHovered(colorRole: ColorRole): string;
        getPressed(colorRole: ColorRole): string;
        getDisabled(colorRole: ColorRole): string;
        getSubduedHovered(colorRole: ColorRole): string | undefined;
        getSubduedPressed(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        hovered: string;
        pressed: string;
        selected: string;
        primary: string;
        primaryHovered: string;
        primaryPressed: string;
        primaryDisabled: string;
        primarySubduedHovered: string;
        primarySubduedPressed: string;
        secondary: string;
        secondaryHovered: string;
        secondaryPressed: string;
        secondaryDisabled: string;
        critical: string;
        criticalHovered: string;
        criticalPressed: string;
        criticalDisabled: string;
        criticalSubdued: string;
        criticalSubduedHovered: string;
        criticalSubduedPressed: string;
        success: string;
        successSecondary: string;
        successHovered: string;
        successPressed: string;
        successDisabled: string;
        successSubdued: string;
        successSubduedHovered: string;
        successSubduedPressed: string;
        highlight: string;
        highlight2: string;
        highlightSubdued: string;
        warning: string;
        warningSecondary: string;
        warningSubdued: string;
        warningSubduedAlt: string;
        decorative1: string;
        decorative1Subdued: string;
        decorative2: string;
        decorative2Subdued: string;
        decorative3: string;
        decorative3Subdued: string;
        decorative4: string;
        decorative4Subdued: string;
        interactive: string;
        interactiveDisabled: string;
        tableSubdued: string;
        nora: string;
        noraDark: string;
        noraGradient: string;
        noraFooter: string;
        noraDivider: string;
        growthHubPremium: string;
    };
    text: {
        getBase(colorRole: ColorRole): string | undefined;
        getPressed(colorRole: ColorRole): string | undefined;
        getDisabled(colorRole: ColorRole): string | undefined;
        getOn(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        disabled: string;
        onSecondary: string;
        primary: string;
        primaryPressed: string;
        primaryDisabled: string;
        onPrimary: string;
        critical: string;
        criticalPressed: string;
        criticalDisabled: string;
        onCritical: string;
        onCriticalSubdued: string;
        success: string;
        successSecondary: string;
        successPressed: string;
        successDisabled: string;
        onSuccess: string;
        onSuccessSubdued: string;
        highlight: string;
        onHighlight: string;
        warning: string;
        onWarning: string;
        onWarningSubdued: string;
        onWarningSubduedAlt: string;
        decorative1: string;
        onDecorative1Subdued: string;
        decorative2: string;
        onDecorative2Subdued: string;
        decorative3: string;
        onDecorative3Subdued: string;
        decorative4: string;
        onDecorative4Subdued: string;
        interactive: string;
        interactiveHovered: string;
        interactivePressed: string;
        interactiveDisabled: string;
        placeHolder: string;
        link: string;
    };
    icon: {
        default: string;
        subdued: string;
        disabled: string;
        primary: string;
        onPrimary: string;
        tertiary: string;
        tertiaryDisabled: string;
        warning: string;
        onWarning: string;
        critical: string;
        criticalDisabled: string;
        onCritical: string;
        success: string;
        successDisabled: string;
        onSuccess: string;
        highlight: string;
        onHighlight: string;
        decorative1: string;
        decorative1Subdued: string;
        decorative2: string;
        decorative2Subdued: string;
        decorative3: string;
        decorative3Subdued: string;
        onDecorative3Subdued: string;
        decorative4: string;
        decorative4Subdued: string;
        decorative4Bright: string;
        interactive: string;
        interactiveHovered: string;
        interactivePressed: string;
        interactiveDisabled: string;
        interactiveCritical: string;
        interactiveCriticalHovered: string;
        interactiveCriticalPressed: string;
        interactiveCriticalDisabled: string;
        focused: string;
    };
    border: {
        getBase(colorRole: ColorRole): string;
        getPressed(colorRole: ColorRole): string;
        getDisabled(colorRole: ColorRole): string;
        getSubdued(colorRole: ColorRole): string | undefined;
        default: string;
        subdued: string;
        hovered: string;
        primary: string;
        primaryPressed: string;
        primaryDisabled: string;
        onPrimary: string;
        secondary: string;
        secondaryPressed: string;
        secondaryDisabled: string;
        onSecondary: string;
        critical: string;
        criticalPressed: string;
        criticalDisabled: string;
        criticalSubdued: string;
        onCritical: string;
        success: string;
        successPressed: string;
        successDisabled: string;
        successSubdued: string;
        onSuccess: string;
        highlight: string;
        highlightSubdued: string;
        warning: string;
        warningSubdued: string;
        warningSubduedAlt: string;
        decorative1: string;
        decorative2: string;
        decorative3: string;
        decorative4: string;
        interactive: string;
        focused: string;
        dark: string;
    };
    divider: {
        default: string;
    };
}

export const createTheme: (options?: ThemeOptions, ..._args: Record<string, unknown>[]) => Theme = (
    options,
    ..._args
) => {
    const actionPalette = getActionPalette('light');
    const optionsWithDefaults = merge(
        {
            // Keep MUI palette available, including custom keys we augment via typings
            palette: {},
            resqPalette: {
                brand: RESQ_BRAND_COLORS,
                background: {
                    default: DASHBOARD_BACKGROUND_COLORS.light,
                },
                surface: {
                    getBase(colorRole: ColorRole): string {
                        return (this as any)[colorRole] as string;
                    },
                    getHovered(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Hovered`] as string;
                    },
                    getPressed(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Pressed`] as string;
                    },
                    getDisabled(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Disabled`] as string;
                    },
                    getSubduedHovered(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}SubduedHovered`];
                    },
                    getSubduedPressed(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}SubduedPressed`];
                    },
                    default: '#FFFFFF',
                    subdued: '#F0F1F1',
                    hovered: '#F0F1F1',
                    pressed: '#EAF1FB',
                    selected: '#EAF1FB',
                    primary: actionPalette.primary.surface,
                    primaryHovered: actionPalette.primary.hovered,
                    primaryPressed: actionPalette.primary.pressed,
                    primaryDisabled: actionPalette.primary.disabled,
                    primarySubduedHovered: actionPalette.primary.subduedHovered,
                    primarySubduedPressed: actionPalette.primary.subduedPressed,
                    secondary: actionPalette.secondary.surface,
                    secondaryHovered: actionPalette.secondary.hovered,
                    secondaryPressed: actionPalette.secondary.pressed,
                    secondaryDisabled: actionPalette.secondary.disabled,
                    // Filled-button surfaces are tuned so white labels reach 4.5:1.
                    critical: '#E02B33',
                    criticalHovered: '#B3272E',
                    criticalPressed: '#881D23',
                    criticalDisabled: '#F28F94',
                    criticalSubdued: '#FDEDEE',
                    criticalSubduedHovered: '#E131391A',
                    criticalSubduedPressed: '#E131394D',
                    success: '#2A865C',
                    successSecondary: '#33A370',
                    successHovered: '#216B49',
                    successPressed: '#195037',
                    successDisabled: '#51BF8C',
                    successSubdued: '#E3F5ED',
                    successSubduedHovered: '#2A865C1A',
                    successSubduedPressed: '#2A865C4D',
                    highlight: '#7B5DE6',
                    highlight2: RESQ_BRAND_COLORS.navy,
                    highlightSubdued: '#F2EFFC',
                    warning: '#F6D06E',
                    warningSecondary: '#EB6A1E',
                    warningSubdued: '#FCF0D0',
                    warningSubduedAlt: '#FDE8DD',
                    decorative1: '#EB6A1E',
                    decorative1Subdued: '#FDEEE5',
                    decorative2: '#00A1A3',
                    decorative2Subdued: '#DBF6F6',
                    decorative3: '#7D60E6',
                    decorative3Subdued: '#F2EFFC',
                    decorative4: '#AB8D40',
                    decorative4Subdued: '#FCF0D0',
                    interactive: '#5693E1',
                    interactiveDisabled: '#C0D6F4',
                    tableSubdued: '#F0F1F1',
                    nora: '#8FA88B',
                    noraDark: '#2F3B34',
                    noraGradient:
                        'conic-gradient(from 124deg at 57.19% 134.3%, #6F935E 0deg, #485B40 360deg)',
                    noraFooter: '#2A3A49',
                    noraDivider: RESQ_BRAND_COLORS.navy,
                    growthHubPremium: RESQ_BRAND_COLORS.navy,
                },
                text: {
                    getBase(colorRole: ColorRole): string | undefined {
                        return (this as any)[colorRole];
                    },
                    getPressed(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Pressed`];
                    },
                    getDisabled(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Disabled`];
                    },
                    getOn(colorRole: ColorRole): string | undefined {
                        return (this as any)[`on${_.capitalize(colorRole)}`];
                    },
                    default: RESQ_BRAND_COLORS.navy,
                    subdued: RESQ_BRAND_COLORS.slate,
                    disabled: '#909097',
                    onSecondary: actionPalette.secondary.onText,
                    primary: actionPalette.primary.text,
                    primaryPressed: actionPalette.primary.textPressed,
                    primaryDisabled: actionPalette.primary.textDisabled,
                    onPrimary: actionPalette.primary.onText,
                    critical: '#B3272E',
                    criticalPressed: '#881D23',
                    criticalDisabled: '#F28F94',
                    onCritical: '#FFFFFF',
                    onCriticalSubdued: RESQ_BRAND_COLORS.navy,
                    success: '#216B49',
                    successSecondary: '#287E57',
                    successPressed: '#195037',
                    successDisabled: '#51BF8C',
                    onSuccess: '#FFFFFF',
                    onSuccessSubdued: RESQ_BRAND_COLORS.navy,
                    highlight: '#6248C5',
                    onHighlight: '#FFFFFF',
                    warning: '#705C29',
                    onWarning: RESQ_BRAND_COLORS.navy,
                    onWarningSubdued: RESQ_BRAND_COLORS.navy,
                    onWarningSubduedAlt: '#B45524',
                    decorative1: '#994613',
                    decorative2: '#00696B',
                    decorative3: '#6248C5',
                    decorative4: '#705C29',
                    interactive: '#0058CA',
                    interactiveHovered: '#004299',
                    interactivePressed: RESQ_BRAND_COLORS.navy,
                    interactiveDisabled: '#909097',
                    placeHolder: '#6F6F77',
                    link: '#005BD1',
                },
                icon: {
                    default: RESQ_BRAND_COLORS.navy,
                    subdued: RESQ_BRAND_COLORS.slate,
                    disabled: '#909097',
                    primary: actionPalette.primary.icon,
                    onPrimary: actionPalette.primary.onText,
                    tertiary: actionPalette.primary.icon,
                    tertiaryDisabled: actionPalette.primary.iconDisabled,
                    warning: '#8D7434',
                    onWarning: RESQ_BRAND_COLORS.navy,
                    critical: '#B3272E',
                    criticalDisabled: '#F28F94',
                    onCritical: '#FFFFFF',
                    success: '#216B49',
                    successDisabled: '#51BF8C',
                    onSuccess: '#FFFFFF',
                    highlight: '#7D60E6',
                    onHighlight: '#FFFFFF',
                    decorative1: '#EB6A1E',
                    decorative1Subdued: '#F4935D',
                    decorative2: '#00A1A3',
                    decorative2Subdued: '#00BEC0',
                    decorative3: '#7D60E6',
                    decorative3Subdued: '#B1A0F0',
                    decorative4: '#AB8D40',
                    decorative4Subdued: '#CAA74B',
                    decorative4Bright: '#F6D06E',
                    interactive: '#0058CA',
                    interactiveHovered: '#004299',
                    interactivePressed: RESQ_BRAND_COLORS.navy,
                    interactiveDisabled: '#909097',
                    interactiveCritical: '#E13139',
                    interactiveCriticalHovered: '#B3272E',
                    interactiveCriticalPressed: '#881D23',
                    interactiveCriticalDisabled: '#ED646B',
                    focused: '#2A76D9',
                },
                border: {
                    getBase(colorRole: ColorRole): string {
                        return (this as any)[colorRole] as string;
                    },
                    getPressed(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Pressed`] as string;
                    },
                    getDisabled(colorRole: ColorRole): string {
                        return (this as any)[`${colorRole}Disabled`] as string;
                    },
                    getSubdued(colorRole: ColorRole): string | undefined {
                        return (this as any)[`${colorRole}Subdued`];
                    },
                    default: '#AAABB0',
                    subdued: '#D4D4D7',
                    hovered: '#909097',
                    primary: actionPalette.primary.border,
                    primaryPressed: actionPalette.primary.borderPressed,
                    primaryDisabled: actionPalette.primary.borderDisabled,
                    onPrimary: 'rgba(15, 16, 26, 0.3)',
                    secondary: actionPalette.secondary.border,
                    secondaryPressed: actionPalette.secondary.borderPressed,
                    secondaryDisabled: actionPalette.secondary.borderDisabled,
                    onSecondary: 'rgba(15, 16, 26, 0.3)',
                    critical: '#E13139',
                    criticalPressed: '#881D23',
                    criticalDisabled: '#F28F94',
                    criticalSubdued: '#ED646B',
                    onCritical: 'rgba(15, 16, 26, 0.3)',
                    success: '#2A865C',
                    successPressed: '#195037',
                    successDisabled: '#51BF8C',
                    successSubdued: '#33A370',
                    onSuccess: 'rgba(15, 16, 26, 0.3)',
                    warning: '#8D7434',
                    warningSubdued: '#AB8D40',
                    warningSubduedAlt: '#F4B184',
                    highlight: '#7D60E6',
                    highlightSubdued: '#9780EB',
                    decorative1: '#EB6A1E',
                    decorative2: '#00A1A3',
                    decorative3: '#7D60E6',
                    decorative4: '#AB8D40',
                    interactive: '#0058CA',
                    focused: '#2A76D9',
                    dark: RESQ_BRAND_COLORS.slate,
                },
                divider: {
                    default: '#D4D4D7',
                },
            },
        },
        options ?? {},
    );
    // Backfill custom palette entries used by MUI components (neutral, danger, payment)
    // so color="neutral"/"danger"/"payment" props keep working.
    const rp = (optionsWithDefaults as any).resqPalette as ThemePalette;
    const pal = (optionsWithDefaults as any).palette as any;
    pal.neutral = pal.neutral ?? {
        // a light neutral for backgrounds/borders
        main: rp.border.subdued ?? rp.border.default,
        contrastText: rp.text.default,
    };
    pal.danger = pal.danger ?? {
        main: rp.surface.critical,
        contrastText: rp.text.onCritical,
    };
    pal.payment = pal.payment ?? {
        main: rp.surface.successSecondary ?? rp.surface.success,
        contrastText: rp.text.onSuccess,
    };
    // Foreground on decorative3Subdued (e.g. the selected nav pill) follows the strong
    // decorative3 color unless a theme sets a dedicated on-color, such as a dark theme
    // whose subdued surface is too dark for the strong color.
    rp.text.onDecorative3Subdued = rp.text.onDecorative3Subdued ?? rp.text.decorative3;
    rp.icon.onDecorative3Subdued = rp.icon.onDecorative3Subdued ?? rp.icon.decorative3;
    const baseTheme = createMuiTheme(optionsWithDefaults, ..._args);

    // Create custom shadows array (must maintain exact length of 25 elements)
    const customShadows = [...baseTheme.shadows] as typeof baseTheme.shadows;
    customShadows[1] = '0px 4px 8px 0px rgba(218, 218, 218, 0.25)';

    // Create final theme with all augmented properties
    const finalTheme = createMuiTheme(
        {
            ...baseTheme,
            shadows: customShadows,
        },
        ..._args,
    );

    return finalTheme;
};
